import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import preview from 'assets/img/preview.jpg';
import error from 'assets/img/static/ico_error404_xl.png';
import { BiCalendarCheck } from 'react-icons/bi';
import { PiBroomBold } from 'react-icons/pi';
import { formatCurrency } from 'utils/currencyFormatter';
// api
import { getMyOrdersWithLang, getDetailOrder, getMinYearOrder, createReturn } from 'api/orders';
// components
import Head from 'components/metatags_helmet/metatags';
import DatePicker from 'components/backoffice/dateTimePicker';
import Pagination from 'components/userzone/Pagination';
import NoResults from 'components/userzone/NoResults';

const OrderList = () => {
  const { t, i18n } = useTranslation(['backoffice', 'home', 'header']);
  const title = t('pedidos');
  const [orders, setOrders] = useState([]);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [minYear, setMinYear] = useState(null);
  const maxYear = new Date().getFullYear();
  const [maxPage, setMaxPage] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedArticles, setSelectedArticles] = useState([]);

  const handleSelectArticle = (id) => {
    setSelectedArticles((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((articleId) => articleId !== id) : [...prevSelected, id],
    );
  };

  const fetchOrders = async () => {
    try {
      const { data, status } = await getMyOrdersWithLang(
        {
          page,
          page_size: 10,
          year: selectedYear || '',
        },
        i18n.language,
      );
      if (status === 200 && Array.isArray(data?.results)) {
        setOrders(data?.results);
        setErrorMessage('');
        setMaxPage(data?.total_pages);
      } else {
        setErrorMessage(t('mensajeerror'));
      }
    } catch (error) {
      setErrorMessage(t('mensajeerror'));
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [t, page, selectedYear]);

  const handleReturn = async (orderId, articleIds) => {
    const payload = {
      pedido: orderId,
      return_status: 0,
      items: articleIds.map((id) => ({ articulo: id })),
    };
    try {
      const response = await createReturn(payload);
      setSelectedArticles([]);
      await fetchOrders();
    } catch (error) {
      console.error('Error al crear el retorno:', error);
    }
  };
  
  const handleToggleDetails = async (orderId) => {
    if (selectedOrderDetails?.id === orderId) {
      setSelectedOrderDetails(null);
      setSelectedArticles([]);
    } else {
      setSelectedArticles([]);
      try {
        const { data, status } = await getDetailOrder(orderId);
        if (status === 200) {
          setSelectedOrderDetails({ id: orderId, ...data });
          setErrorMessage('');
        } else {
          setErrorMessage(t('mensajeerror'));
        }
      } catch (error) {
        setErrorMessage(t('mensajeerror'));
      }
    }
  };

  const renderHeader = () => {
    return <span className="fw-bold">{minYear !== maxYear ? `${minYear} - ${maxYear}` : `${maxYear}`}</span>;
  };

  const handleSelectedYear = (date) => {
    setSelectedYear(date.getFullYear());
    setPage(1);
  };

  const handleClean = () => {
    setSelectedYear(null);
    setPage(1);
  };

  useEffect(() => {
    const getMinYear = async () => {
      const year = await getMinYearOrder();
      if (year.status === 200) setMinYear(year.data?.min_year);
      else setMinYear(maxYear);
    };
    getMinYear();
  }, []);

  return (
    <Container fluid className="margin-top-md mx-3 d-flex mainDiv order-list">
      <Head title={title} noIndex={true} />
      <Row className="w-100">
        <Col md={12} className="d-flex flex-row justify-content-between datetimepicker-userzone">
          <h1 className="page-title static-title d-flex justify-content-start">{t('pedidos')}</h1>
          <div className="d-flex justify-content-end gap-3">
            <DatePicker
              className="d-flex justify-content-end form-control"
              value={selectedYear ? new Date(selectedYear, 0, 1) : ''}
              onChange={handleSelectedYear}
              dateFormat="yyyy"
              showYearPicker={true}
              showTimeInput={false}
              placeholderText={t('filterByYear')}
              showIcon={true}
              icon={<BiCalendarCheck />}
              minDate={new Date(minYear, 0, 1)}
              maxDate={new Date(maxYear, 11, 31)}
              renderCustomHeader={renderHeader}
            />
            <div className="close-search mt-2">
              <PiBroomBold title={t('clean')} onClick={handleClean} />
            </div>
          </div>
        </Col>
        {errorMessage ? (
          <Col md={12}>
            <div className="text-center">
              <img className="mb-1" src={error} alt="error.png" />
              <h2 className="mb-1">{errorMessage}</h2>
              <Link to="/" className="mt-4 d-block">
                <Button className="p-3">{t('gobackhome', { ns: 'home' }).toUpperCase()}</Button>
              </Link>
            </div>
          </Col>
        ) : orders.length > 0 ? (
          <>
            <Col md={12} className="mb-2 d-none d-md-block">
              <Card className="card-headers">
                <Row>
                  <Col
                    md={1}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Title>
                      <strong className="txt-headers">{t('promoCodes.code')}</strong>
                    </Card.Title>
                  </Col>
                  <Col
                    md={1}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('date')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.paymentMethod')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.paymentStatus')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.orderStatus')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('product.totalPrice')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-end justify-content-center text-center text-md-right mb-3 mb-md-0"
                  >
                    <Card.Text className="px-1">
                      <strong className="txt-headers">{t('orderDetails')}</strong>
                    </Card.Text>
                  </Col>
                </Row>
              </Card>
            </Col>
            {orders.map((order) => (
              <Col md={12} key={order.id} className="mb-2">
                <Card>
                  <Row>
                    <Col
                      md={1}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Title className="mb-0">#{order.code}</Card.Title>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Title>
                          {t('promoCodes.code')}: #{order.code}
                        </Card.Title>
                      </div>
                    </Col>
                    <Col
                      md={1}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.fecha}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('date')}: {order.fecha}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.payment_method}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {order.payment_method}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.payment_status_display}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {order.payment_status_display}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.status.text}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {order.status.text}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{formatCurrency(order.total)}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('product.totalPrice')}: {formatCurrency(order.total)}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center align-items-md-end justify-content-center text-center text-md-left mb-3 mb-md-0 col-md-2"
                    >
                      <Button
                        className="button-secondary card-button btn-secondary"
                        onClick={() => handleToggleDetails(order.id)}
                      >
                        {selectedOrderDetails?.id === order.id ? (
                          <AiFillEyeInvisible className="icon-md" />
                        ) : (
                          <AiFillEye className="icon-md" />
                        )}
                        <span className="mx-2">{t('verDetalle')}</span>
                      </Button>
                    </Col>
                  </Row>
                  {selectedOrderDetails?.id === order.id && (
                    <Row className="mt-4">
                      {selectedOrderDetails?.articles?.length > 0 ? (
                        <>
                          <h5 className="mb-3 d-md-none text-center">{t('productos')}</h5>
                          <Col md={12} className="mb-2 d-none d-md-block">
                            <Card className="card-productos">
                              <Row>
                                <Col md={5} className="d-flex flex-column justify-content-center mb-3 mb-md-0">
                                  <Card.Title>
                                    <strong className="txt-headers">{t('productos')}</strong>
                                  </Card.Title>
                                </Col>
                                <Col md={3} className="d-flex flex-column justify-content-center mb-3 mb-md-0">
                                  <Card.Text>
                                    <strong className="txt-headers">{t('product.editorial')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('product.quantity')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('price')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={2}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Button
                                    className="w-70"
                                    variant="danger"
                                    disabled={selectedArticles.length === 0}
                                    onClick={() => handleReturn(order.id, selectedArticles)}
                                  >
                                    {t('return')}
                                  </Button>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                          {selectedOrderDetails?.articles?.map((item, index) => (
                            <Col md={12} key={`${item.product.id}-${index}`} className="mb-2">
                              <Card className="card-internal">
                                <Row>
                                  <Col
                                    md={5}
                                    className="container-detail-product text-md-left mb-3 mb-md-0 justify-content-center justify-content-md-start"
                                  >
                                    <img
                                      className="card-img"
                                      src={item.product.cover || preview}
                                      alt={item.product.editorial}
                                    />
                                    <Card.Title className="mb-0 ">
                                      <Link to={`/libro/${item.product.id}`} className="title-product ">
                                        {item.product.title}
                                      </Link>
                                    </Card.Title>
                                  </Col>
                                  <Col md={3} className="d-flex flex-column mb-3 mb-md-0">
                                    <div className="d-none d-md-flex h-100 align-items-center">
                                      <div className="d-flex flex-column justify-content-center">
                                        <Card.Text className="mb-0">{item.product.editorial}</Card.Text>
                                      </div>
                                    </div>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('product.editorial')}: {item.product.editorial}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">{item.quantity}</Card.Text>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('product.quantity')}: {item.quantity}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">
                                      {formatCurrency(item.precio_venta)}
                                    </Card.Text>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('price')}: {formatCurrency(item.precio_venta)}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={2}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      className="me-2 d-none d-md-flex"
                                      checked={selectedArticles.includes(item.id)}
                                      onChange={() => handleSelectArticle(item.id)}
                                    />
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <Form.Check
                                          type="checkbox"
                                          className="mb-3"
                                          checked={selectedArticles.includes(item.id)}
                                          onChange={() => handleSelectArticle(item.id)}
                                        />
                                      </Card.Text>
                                      <Button
                                        variant="danger"
                                        disabled={selectedArticles.length === 0}
                                        onClick={() => handleReturn(order.id, selectedArticles)}
                                      >
                                        {t('return')}
                                      </Button>
                                    </div>
                                  </Col>
                                </Row>
                              </Card>
                            </Col>
                          ))}
                        </>
                      ) : (
                        <Card className="card-productos">
                          <Row>
                            <Col md={12} className="text-center">
                              <Card.Title>
                                <strong className="txt-headers">{t('noproducts', { ns: 'header' })}</strong>
                              </Card.Title>
                            </Col>
                          </Row>
                        </Card>
                      )}
                    </Row>
                  )}
                </Card>
              </Col>
            ))}
            <Col md={12}>
              <Pagination changePage={setPage} page={page} totalPages={maxPage} />
            </Col>
          </>
        ) : (
          <Col md={12}>
            <NoResults className="noFavorites" />
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default OrderList;
